import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/solid';
import { useGames } from '../contexts/DataContext';
import { GameOutput } from '../generated-api';
import ChatPage from './ChatPage';

const RulesChatToolPage: React.FC = () => {
  const navigate = useNavigate();
  const { games, loading: gamesLoading, fetch: fetchGames } = useGames();
  const [selectedGame, setSelectedGame] = useState<GameOutput | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  const sortedFilteredGames = useMemo(() => {
    const filtered = games.filter((g) =>
      g.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );
    return filtered.sort((a, b) => {
      const aHasRules = !!a.rulesSummary;
      const bHasRules = !!b.rulesSummary;
      if (aHasRules !== bHasRules) return aHasRules ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [games, searchQuery]);

  // Chat active state
  if (selectedGame) {
    return (
      <div className="fixed inset-0 z-30 pb-20 sm:pb-0 sm:static sm:h-[calc(100vh-68px)] flex flex-col overflow-hidden bg-surface-base">
        {/* Compact game header */}
        <div className="flex-shrink-0 relative pt-3 sm:pt-4 pb-3 px-4 border-b border-stone-200 bg-surface">
          <button
            onClick={() => setSelectedGame(null)}
            className="absolute left-4 top-3 sm:top-4 p-2 -ml-2 text-body hover:text-heading"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div className="text-center">
            <h1 className="text-fluid-h2 font-bold text-heading">Rules Chat</h1>
            <div className="flex items-center justify-center gap-2 mt-1">
              {selectedGame.imageUrl ? (
                <img
                  src={selectedGame.imageUrl}
                  alt={selectedGame.name}
                  className="w-5 h-5 rounded object-cover"
                />
              ) : null}
              <span className="text-sm text-muted">{selectedGame.name}</span>
              <button
                onClick={() => setSelectedGame(null)}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium ml-1"
              >
                Change
              </button>
            </div>
          </div>
        </div>

        {/* Chat — fill remaining height, prevent outer scroll */}
        <div className="flex-1 min-h-0 px-4 py-3">
          <ChatPage gameId={selectedGame.id} gameName={selectedGame.name} />
        </div>
      </div>
    );
  }

  // Game selection state
  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <div className="relative pt-14 sm:pt-4 pb-3 px-4 bg-surface border-b border-stone-200">
        <button
          onClick={() => navigate(-1)}
          className="absolute left-4 top-14 sm:top-4 p-2 -ml-2 text-body hover:text-heading"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <h1 className="text-fluid-h2 font-bold text-heading text-center">Rules Chat</h1>
        <p className="text-center text-sm text-stone-500 mt-1">
          Pick a game to ask about its rules
        </p>
      </div>

      {/* Search */}
      <div className="px-4 pt-3 pb-2 bg-surface border-b border-stone-200">
        <div className="relative">
          <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            type="text"
            placeholder="Search games..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 text-sm border border-stone-300 rounded-xl bg-stone-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Game List */}
      <div className="flex-1 overflow-y-auto px-4 py-3 pb-24 sm:pb-8">
        {gamesLoading && games.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : sortedFilteredGames.length === 0 ? (
          <div className="text-center py-12 text-stone-400">
            <p className="text-lg font-medium">No games found</p>
            <p className="text-sm mt-1">Try adjusting your search</p>
          </div>
        ) : (
          <div className="space-y-2">
            {sortedFilteredGames.map((game) => {
              const hasRules = !!game.rulesSummary;
              return (
                <button
                  data-track="rules_chat_game_card"
                  key={game.id}
                  onClick={() => {
                    if (hasRules) {
                      setSelectedGame(game);
                    }
                  }}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors text-left ${
                    hasRules
                      ? 'bg-surface border-stone-200 hover:bg-stone-50 cursor-pointer'
                      : 'bg-stone-50 border-stone-100 opacity-60 cursor-default'
                  }`}
                >
                  {/* Rules indicator */}
                  <div
                    className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                      hasRules ? 'bg-green-500' : 'bg-stone-300'
                    }`}
                  />

                  {/* Game Image */}
                  {game.imageUrl ? (
                    <img
                      src={game.imageUrl}
                      alt={game.name}
                      className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-stone-200 flex items-center justify-center flex-shrink-0">
                      <ChatBubbleLeftRightIcon className="w-5 h-5 text-stone-400" />
                    </div>
                  )}

                  {/* Game Info */}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-stone-900 truncate">{game.name}</div>
                    <div className="text-xs text-stone-500 mt-0.5">
                      {hasRules ? 'Rules available' : 'No rules — upload from game page'}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RulesChatToolPage;
